import type { CVListItemDto } from '@cibli/shared';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { downloadCV } from '@/services/cv';

export type DownloadableCV = Pick<CVListItemDto, 'id' | 'firstName' | 'lastName'>;

interface UseDownloadCVReturn {
  downloadCVFromList: (cv: DownloadableCV) => Promise<void>;
  isDownloading: boolean;
  downloadingId: string | null;
  error: string | null;
}

export function useDownloadCV(): UseDownloadCVReturn {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const downloadCVFromList = async (cv: DownloadableCV) => {
    let toastId: string | number | undefined;
    try {
      setIsDownloading(true);
      setDownloadingId(cv.id);
      setError(null);
      toastId = toast.loading(t('dashboard.cvList.download.downloading'));

      const pdfBlob = await downloadCV(cv.id);

      // Create a blob URL from the PDF blob
      const url = URL.createObjectURL(pdfBlob);

      const firstName = cv.firstName?.trim();
      const lastName = cv.lastName?.trim();
      const filename = [firstName, lastName].filter(Boolean).join('-') || cv.id;

      // Create an anchor element and trigger download
      const a = document.createElement('a');
      a.href = url;
      a.download = `CV-${filename}.pdf`;
      document.body.appendChild(a);
      a.click();

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      toast.success(t('dashboard.cvList.download.success'), { id: toastId });
    } catch (err) {
      console.error('Error downloading CV:', err);
      const message = t('dashboard.cvList.download.error');
      setError(message);
      toast.error(message, toastId ? { id: toastId } : undefined);
    } finally {
      setIsDownloading(false);
      setDownloadingId(null);
    }
  };

  return {
    downloadCVFromList,
    isDownloading,
    downloadingId,
    error,
  };
}
